import { Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertySpotlightV2WebApiHandler_exclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_pin,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_reassignWithOptimization,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_unexclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_unpin,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import {
  buildAdvertisingQueryPredicate,
  getCurrentPlacementSource,
  updateAdvertisingCache,
} from '../utils/cacheHelpers';
import { applyCountDeltas, calculateCountDeltas, type CountDeltas } from '../utils/countCalculator';

import type { PlacementSource } from '@zg-rentals/ts-rental-manager-api-client';

type SpotlightCounts = {
  pinnedCount: number;
  autoCount: number;
  availableCount: number;
};

type MutationConfig = {
  labelFor: (id: string) => string;
  packageFor: (id: string) => string;
  spotlightCounts: SpotlightCounts | null;
  setSpotlightCounts: React.Dispatch<React.SetStateAction<SpotlightCounts | null>>;
};

/**
 * Hook that encapsulates all spotlight mutation logic with optimistic updates
 */
export const useSpotlightMutations = (config: MutationConfig) => {
  const { spotlightCounts, setSpotlightCounts } = config;
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: pinMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_pin({
    meta: {
      invalidates: ['getProperties', 'spotlightOverview'],
      context: { api: 'spotlight', details: 'pin listing to spotlight' },
    },
  });

  const { mutateAsync: unpinMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_unpin({
    meta: {
      invalidates: ['getProperties', 'spotlightOverview'],
      context: { api: 'spotlight', details: 'unpin listing from spotlight' },
    },
  });

  const { mutateAsync: excludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_exclude({
    meta: {
      invalidates: ['getProperties', 'spotlightOverview'],
      context: { api: 'spotlight', details: 'exclude listing from spotlight' },
    },
  });

  const { mutateAsync: unexcludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_unexclude({
    meta: {
      invalidates: ['getProperties', 'spotlightOverview'],
      context: { api: 'spotlight', details: 'unexclude listing from spotlight' },
    },
  });

  const { mutateAsync: reassignMutation } =
    useRentalManagerUserPropertySpotlightV2WebApiHandler_reassignWithOptimization({
      meta: {
        invalidates: ['getProperties', 'spotlightOverview'],
        context: { api: 'spotlight', details: 'reassign spotlight with optimization' },
      },
    });

  /**
   * Optimistically update overview counts using setState updater function
   */
  const optimisticallyUpdateOverview = (deltas: CountDeltas) => {
    setSpotlightCounts((prev) => {
      if (!prev) return prev;
      return applyCountDeltas(prev, deltas);
    });
  };

  /**
   * Generic mutation executor with optimistic updates and rollback
   */
  const executeMutation = async <TSnapshot extends Record<string, unknown>>({
    id,
    action,
    mutationFn,
    targetSource,
    countDeltas,
    successMessage,
    successTone = 'success',
    errorMessage,
    applyOptimisticUpdate,
    captureSnapshot,
    restoreSnapshot,
  }: {
    id: string;
    action: 'pin' | 'unpin' | 'exclude' | 'unexclude';
    mutationFn: () => Promise<unknown>;
    targetSource: PlacementSource | null;
    countDeltas?: CountDeltas;
    successMessage: string;
    successTone?: 'success' | 'info';
    errorMessage: string;
    applyOptimisticUpdate: () => void;
    captureSnapshot: () => TSnapshot;
    restoreSnapshot: (snapshot: TSnapshot) => void;
  }) => {
    // Capture current state for rollback BEFORE any changes
    const snapshot = captureSnapshot();
    const previousCounts = spotlightCounts;
    const currentSource = getCurrentPlacementSource(queryClient, id);
    const deltas = countDeltas ?? calculateCountDeltas(action, currentSource);

    // Apply optimistic updates
    applyOptimisticUpdate();
    updateAdvertisingCache(queryClient, id, targetSource);
    optimisticallyUpdateOverview(deltas);

    try {
      await mutationFn();
      // Success - keep optimistic update
      enqueueToast(
        <Toast id={`spotlight-${action}`} tone={successTone}>
          {successMessage}
        </Toast>,
      );
    } catch (error) {
      // Rollback on error. Restore the exact pre-mutation counts — re-applying inverted deltas is not
      // symmetric because applyCountDeltas clamps at 0.
      restoreSnapshot(snapshot);
      updateAdvertisingCache(queryClient, id, currentSource ?? null);
      setSpotlightCounts(previousCounts);
      enqueueToast(
        <Toast id={`spotlight-${action}-error`} tone="critical">
          {errorMessage}
        </Toast>,
      );
      logError(error);
    }
  };

  return {
    executeMutation,
    optimisticallyUpdateOverview,
    pinMutation,
    unpinMutation,
    excludeMutation,
    unexcludeMutation,
    reassignMutation,
    updateAdvertisingCache: (id: string, source: PlacementSource | null) =>
      updateAdvertisingCache(queryClient, id, source),
    getCurrentPlacementSource: (id: string) => getCurrentPlacementSource(queryClient, id),
    invalidateAdvertisingQuery: (id: string) => {
      return queryClient.invalidateQueries(buildAdvertisingQueryPredicate(id));
    },
  };
};
